import {
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import type { z } from "zod";

import {
  getAllGoalByUserIdFn,
  createGoalOfLifeFn,
  updateGoalOfLifeFn,
  deleteGoalOfLifeFn,
  getSharedGoalsFn,
  setGoalOfLifeDoneFn,
} from "~/lib/functions/goal-of-life";
import type {
  CreateGoalOfLife,
  UpdateGoalOfLife,
} from "~/lib/schemas/goal-of-life";

import type { GoalStatus, GoalTimeline } from "../../../drizzle/schema";

export const goalOfLifeQueryKeys = {
  all: ["goals-of-life"] as const,
  list: (filter?: {
    status?: GoalStatus;
    categoryId?: string;
    timeline?: GoalTimeline;
  }) => [...goalOfLifeQueryKeys.all, "list", filter] as const,
  sharedList: (
    userId: string,
    filter?: {
      status?: GoalStatus;
      categoryId?: string;
      timeline?: GoalTimeline;
    },
  ) => [...goalOfLifeQueryKeys.all, "shared-list", { userId, filter }] as const,
};

export const goalsOfLifeQueryOptions = (filter?: {
  status?: GoalStatus;
  categoryId?: string;
  timeline?: GoalTimeline;
}) =>
  queryOptions({
    queryKey: goalOfLifeQueryKeys.list(filter),
    queryFn: () => getAllGoalByUserIdFn({ data: { filter: filter ?? {} } }),
  });

export const useGoalsOfLife = (filter?: {
  status?: GoalStatus;
  categoryId?: string;
  timeline?: GoalTimeline;
}) => {
  return useQuery(goalsOfLifeQueryOptions(filter));
};

/**
 * Refreshes the goal lists. A save settles only once they hold the saved
 * goal, so no view decrypts a stale ciphertext with the goal's new key.
 */
function useRefreshGoals() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: goalOfLifeQueryKeys.all });
}

export const useCreateGoalOfLife = () => {
  const onSuccess = useRefreshGoals();
  return useMutation({
    mutationFn: (data: z.infer<typeof CreateGoalOfLife>) =>
      createGoalOfLifeFn({ data }),
    onSuccess,
  });
};

export const useUpdateGoalOfLife = () => {
  const onSuccess = useRefreshGoals();
  return useMutation({
    mutationFn: (data: z.infer<typeof UpdateGoalOfLife>) =>
      updateGoalOfLifeFn({ data }),
    onSuccess,
  });
};

/** The owner marks a goal done, or reopens it; the list and its counts refresh. */
export const useSetGoalOfLifeDone = () => {
  const onSuccess = useRefreshGoals();
  return useMutation({
    mutationFn: (data: { id: string; done: boolean }) =>
      setGoalOfLifeDoneFn({ data }),
    onSuccess,
  });
};

export const useDeleteGoalOfLife = () => {
  const onSuccess = useRefreshGoals();
  return useMutation({
    mutationFn: (id: string) => deleteGoalOfLifeFn({ data: { id } }),
    onSuccess,
  });
};

export const sharedGoalsQueryOptions = (
  userId: string | undefined,
  filter?: {
    status?: GoalStatus;
    categoryId?: string;
    timeline?: GoalTimeline;
  },
) =>
  queryOptions({
    queryKey: goalOfLifeQueryKeys.sharedList(userId ?? "", filter),
    queryFn: () =>
      getSharedGoalsFn({
        data: { userId: userId ?? "", filter: filter ?? {} },
      }),
    enabled: !!userId,
  });

export const useSharedGoals = (
  userId: string | undefined,
  filter?: {
    status?: GoalStatus;
    categoryId?: string;
    timeline?: GoalTimeline;
  },
) => {
  return useQuery(sharedGoalsQueryOptions(userId, filter));
};
